/**
 * Cross-Source Correlation — connects events from different source types
 * that are temporally (1 hour) and spatially (100km) proximate.
 *
 * Algorithm:
 * 1. Filter to recent events with location data (excluding high-frequency position updates)
 * 2. Build spatial clusters using greedy proximity grouping
 * 3. Filter to clusters with 2+ different source types
 * 4. Confidence scoring:
 *    - 2 source types = 0.5 base, 3+ = 0.7
 *    - +0.1 per additional source beyond 2
 *    - +0.1 if authority tier 1 source involved
 *    - Capped at 1.0
 * 5. Classify: news-data-alignment, multi-source-convergence, cascade-detection
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { CorrelationSignal } from './types';
import { getEventTimestamp } from './types';
import { haversineDistance } from '@/utils/geo';

const ONE_HOUR_MS = 60 * 60 * 1000;
const PROXIMITY_KM = 100;

/** Authority tier 1 sources (government/authoritative data). */
const AUTHORITY_SOURCES = new Set(['usgs', 'gdacs', 'acled', 'sanctions']);

/** Structured data sources (non-news). */
const DATA_SOURCES = new Set([
  'usgs', 'firms', 'acled', 'opensky', 'vesselfinder', 'gdacs',
]);

/** News/information sources. */
const NEWS_SOURCES = new Set(['gdelt', 'rss']);

/** High-frequency position event types — excluded from correlation to avoid noise. */
const POSITION_EVENT_TYPES = new Set(['flight_position', 'vessel_position']);

interface EventCluster {
  events: NormalizedEvent[];
  sources: Set<string>;
  types: Set<string>;
  centerLat: number;
  centerLng: number;
}

/**
 * Detect cross-source correlations — events from different sources
 * within 1 hour and 100km of each other.
 */
export function detectCorrelations(
  events: NormalizedEvent[],
): CorrelationSignal[] {
  const cutoff = Date.now() - ONE_HOUR_MS;

  // Only consider recent events with valid location data,
  // excluding high-frequency position updates that would create noise
  const recent = events.filter(
    (e) =>
      e.location?.lat != null &&
      e.location?.lng != null &&
      !POSITION_EVENT_TYPES.has(e.type) &&
      getEventTimestamp(e) >= cutoff,
  );

  if (recent.length < 2) return [];

  // Build spatial clusters using greedy grouping
  const clusters: EventCluster[] = [];
  const assigned = new Set<number>();

  for (let i = 0; i < recent.length; i++) {
    if (assigned.has(i)) continue;

    const ev = recent[i];
    const evTs = getEventTimestamp(ev);
    const cluster: EventCluster = {
      events: [ev],
      sources: new Set([ev.source]),
      types: new Set([ev.type]),
      centerLat: ev.location!.lat,
      centerLng: ev.location!.lng,
    };
    assigned.add(i);

    for (let j = i + 1; j < recent.length; j++) {
      if (assigned.has(j)) continue;

      const other = recent[j];
      const dist = haversineDistance(
        cluster.centerLat,
        cluster.centerLng,
        other.location!.lat,
        other.location!.lng,
      );

      if (dist <= PROXIMITY_KM) {
        const timeDiff = Math.abs(evTs - getEventTimestamp(other));
        if (timeDiff <= ONE_HOUR_MS) {
          cluster.events.push(other);
          cluster.sources.add(other.source);
          cluster.types.add(other.type);
          assigned.add(j);
        }
      }
    }

    clusters.push(cluster);
  }

  // Filter to clusters with 2+ different source types and produce signals
  const correlations: CorrelationSignal[] = [];
  const now = new Date().toISOString();
  const seenGridCells = new Set<string>();

  for (const cluster of clusters) {
    if (cluster.sources.size < 2) continue;

    // Deduplication: one correlation per grid cell (1-degree)
    const gridKey = `${Math.floor(cluster.centerLat)}:${Math.floor(cluster.centerLng)}`;
    if (seenGridCells.has(gridKey)) continue;
    seenGridCells.add(gridKey);

    // Confidence scoring
    let confidence: number;
    if (cluster.sources.size >= 3) {
      confidence = 0.7;
    } else {
      confidence = 0.5;
    }

    // +0.1 per additional source beyond 2
    confidence += Math.max(0, cluster.sources.size - 2) * 0.1;

    // +0.1 if authority tier 1 source is involved
    if (cluster.events.some((e) => AUTHORITY_SOURCES.has(e.source))) {
      confidence += 0.1;
    }

    confidence = Math.min(1.0, confidence);

    // Determine correlation type
    const hasNewsSource = cluster.events.some(
      (e) => NEWS_SOURCES.has(e.source),
    );
    const hasDataSource = cluster.events.some(
      (e) => DATA_SOURCES.has(e.source),
    );

    let type: CorrelationSignal['type'];
    if (hasNewsSource && hasDataSource) {
      type = 'news-data-alignment';
    } else if (cluster.sources.size >= 3) {
      type = 'multi-source-convergence';
    } else {
      type = 'cascade-detection';
    }

    // Build description
    const sourceList = Array.from(cluster.sources).join(', ');
    const typeList = Array.from(cluster.types).join(', ');
    const description =
      `${cluster.events.length} events from ${cluster.sources.size} sources ` +
      `(${sourceList}) within ${PROXIMITY_KM}km. Types: ${typeList}.`;

    correlations.push({
      id: `corr-${gridKey}-${cluster.sources.size}-${Date.now()}`,
      type,
      description,
      confidence: Math.round(confidence * 100) / 100,
      sources: Array.from(cluster.sources),
      detectedAt: now,
    });
  }

  // Sort by confidence descending
  correlations.sort((a, b) => b.confidence - a.confidence);

  return correlations;
}

// ---------------------------------------------------------------------------
// Semantic similarity via browser embeddings (optional enhancement)
// ---------------------------------------------------------------------------

/** Cached embeddings for event IDs. */
const embeddingCache = new Map<string, number[]>();
const MAX_EMBEDDING_CACHE = 500;

/**
 * Compute cosine similarity between two vectors.
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}

/**
 * Compute semantic similarity between event pairs using browser-side embeddings.
 * Returns pairs above the similarity threshold.
 * Only works when the browser ML provider is available — fails silently otherwise.
 */
export async function computeSemanticCorrelations(
  events: NormalizedEvent[],
  threshold: number = 0.65,
): Promise<{ eventA: string; eventB: string; similarity: number }[]> {
  // Only try if we have the browser embeddings function available
  let computeEmbeddings: typeof import('@/intelligence/ai/providers/browser-provider').computeBrowserEmbeddings;
  try {
    const mod = await import('@/intelligence/ai/providers/browser-provider');
    computeEmbeddings = mod.computeBrowserEmbeddings;
  } catch {
    return [];
  }

  // Filter to events not yet cached, limit batch size
  const uncached = events.filter(e => !embeddingCache.has(e.id)).slice(0, 50);

  if (uncached.length > 0) {
    try {
      const texts = uncached.map(e => `${e.title} ${e.description ?? ''}`);
      const embeddings = await computeEmbeddings(texts);

      for (let i = 0; i < uncached.length; i++) {
        if (embeddings[i] && embeddings[i].length > 0) {
          embeddingCache.set(uncached[i].id, embeddings[i]);
        }
      }

      // Evict oldest if cache too large
      if (embeddingCache.size > MAX_EMBEDDING_CACHE) {
        const keys = Array.from(embeddingCache.keys());
        for (let i = 0; i < keys.length - MAX_EMBEDDING_CACHE; i++) {
          embeddingCache.delete(keys[i]);
        }
      }
    } catch {
      // Browser ML not available — silently skip
      return [];
    }
  }

  // Compare cached events pairwise (limit to recent 100 for performance)
  const recent = events.slice(0, 100).filter(e => embeddingCache.has(e.id));
  const results: { eventA: string; eventB: string; similarity: number }[] = [];

  for (let i = 0; i < recent.length; i++) {
    const embA = embeddingCache.get(recent[i].id)!;
    for (let j = i + 1; j < recent.length; j++) {
      // Skip same source (we want cross-source correlations)
      if (recent[i].source === recent[j].source) continue;

      const embB = embeddingCache.get(recent[j].id)!;
      const sim = cosineSimilarity(embA, embB);

      if (sim >= threshold) {
        results.push({
          eventA: recent[i].id,
          eventB: recent[j].id,
          similarity: Math.round(sim * 1000) / 1000,
        });
      }
    }
  }

  return results.sort((a, b) => b.similarity - a.similarity).slice(0, 20);
}
